import { DrawerSection } from '@components/ui/drawer/section';
import { useTranslation } from 'react-i18next';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { Tabs } from '@components/ui/tabs';
import { ReactNode, useState } from 'react';
import { Avatar } from '@components/avatar';
import { cn } from '@utils/cn';
import { checkScheduleDisabled, getScheduleDisplayDate } from './helpers';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import { RelatedMultischedule } from '@services/schedules/internal/types';
import { useAppContext } from '@contexts/app';

export const SchedulerSidebarDays = () => {
  const { disabled, relatedMultischedules, crewChangeScope, currentSchedule } =
    useSchedulerSidebar();
  const { tenant } = useAppContext();
  const { t } = useTranslation('app', { keyPrefix: 'schedule.days' });
  const { t: tTooltip } = useTranslation('app', { keyPrefix: 'schedule.crew.tooltip' });

  const [week, setWeek] = useState('1');

  if (!relatedMultischedules) return null;

  const count = Object.values(relatedMultischedules).reduce((acc, val) => acc + val.length, 0);
  if (!count) return null;

  const isCurrentSchedule = (id: string) => id === currentSchedule.id;

  const shouldShowTooltip = (schedule: RelatedMultischedule) => {
    const { id } = schedule;
    if (
      !checkScheduleDisabled({ schedule, currentSchedule, crewChangeScope, zone: tenant.zone }) &&
      !isCurrentSchedule(id)
    )
      return false;
    return true;
  };

  const withTooltip = (schedule: RelatedMultischedule, component: ReactNode) => {
    const { sameCrew, sameForeman, completed, id } = schedule;
    if (!shouldShowTooltip(schedule)) return component;

    const getTooltip = () => {
      if (isCurrentSchedule(id)) return tTooltip('currentSchedule');
      if (completed) return tTooltip('completed');
      if (!sameCrew && !sameForeman) return tTooltip('differentCrewAndForeman');
      if (!sameCrew) return tTooltip('differentCrew');
      if (!sameForeman) return tTooltip('differentForeman');

      return tTooltip('disabled');
    };

    return (
      <AppTooltip side="left" context={<p>{getTooltip()}</p>}>
        {component}
      </AppTooltip>
    );
  };

  const weekNumbers = Object.keys(relatedMultischedules);

  return (
    <DrawerSection
      title={t('title', { count })}
      disabled={disabled}
      className="p-2"
      titleClassName="text-xs font-semibold">
      <div className="flex flex-col gap-2">
        <p className="text-xs font-normal text-neutral-500">{t('message')}</p>
        <div className="flex items-center gap-2.5">
          <span className="text-xs font-medium text-neutral-500">{t('weeks')}</span>
          <Tabs value={week} setValue={setWeek} options={weekNumbers} size="sm" />
        </div>
        {relatedMultischedules[week].map((el) => (
          <div className="flex flex-col gap-2 px-2" key={el.id}>
            {withTooltip(
              el,
              <div
                className={cn(
                  'grid grid-cols-[24px_auto] gap-2 items-center justify-items-start',
                  shouldShowTooltip(el) && 'cursor-pointer',
                )}>
                <Avatar className="h-6 w-6 text-xs" name={el.foreman?.name || ''} />
                <span
                  className={cn(
                    'text-xs justify-self-start',
                    checkScheduleDisabled({
                      schedule: el,
                      currentSchedule,
                      crewChangeScope,
                      zone: tenant.zone,
                    })
                      ? 'text-neutral-300'
                      : 'text-neutral-500',
                  )}>
                  {getScheduleDisplayDate(el, tenant.zone)}
                </span>
                {!el.defaultCrew && (
                  <span className="text-[10px] font-medium bg-neutral-50 text-neutral-500 col-start-2 row-start-2 col-end-3 rounded-md px-2.5 py-1">
                    {t('customCrew')}
                  </span>
                )}
              </div>,
            )}
          </div>
        ))}
      </div>
    </DrawerSection>
  );
};
